import { useState, useEffect, useRef } from "react";
import { toGrams, formatWeightFromGrams } from "../../utils/weightUtils";
import { useParams, useNavigate } from "react-router-dom";
import "./COA.css";
import { useLicense } from "../../context/licenseContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import TBarChart from "../../components/BarChart/TBarChart";
import CBarChart from "../../components/BarChart/CBarChart";
import PdfViewerModal from "../../components/PdfViewerModal/PdfViewerModal";
import ImageViewerModal from "../../components/ImageViewerModal/ImageViewerModal";
import ColorDot from "../../components/ColorDot/ColorDot";
import apiClient from "../../api/apiClient";
import Loading from "../../components/Loading/Loading";
import CoaToShowroom from "../../components/CoaToShowroom/CoaToShowroom";
import COAUpload from "../../components/COAUpload/COAUpload";
import OrderTestModal from "../../components/OrderTestModal/OrderTestModal";
import {
    MAJOR_CANNABINOIDS,
    MINOR_CANNABINOIDS,
    SYNTHETIC_CANNABINOIDS,
    ALL_TERPENES,
} from "./coaFieldConfig";
import { getPanelStatus, isCOAComplete, isThcCompliant, PANEL_LABELS } from "../../utils/coaPanelStatus";
import { useCoaJobs } from "../../context/coaJobsContext";
import { getColor } from "../../utils/colorfunctions";
import { QRCodeSVG } from "qrcode.react";
import { toSlug, BADGE_META } from "../../utils/badgeMeta";
import ProductGrowingDetails from "../../components/ProductGrowingDetails/ProductGrowingDetails";


// Returns [{key, label, value}] for fields that have a positive value in `row`
const filterEntries = (fields, row) =>
    fields
        .map(({ key, label }) => ({ key, label, value: parseFloat(row[key]) || 0 }))
        .filter(({ value }) => value > 0);

// Format a date string; returns null if epoch/invalid
const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("1970-01-01")) return null; 
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};


function qualityBadgeClass(value) {
    const v = (value || "").toLowerCase();
    if (v.includes("pass")) return "coa-page__quality-badge--pass";
    if (v.includes("fail")) return "coa-page__quality-badge--fail";
    return "coa-page__quality-badge--neutral";
}

const TYPE_LABELS = {
    "Type I":   "Type I",
    "Type II":  "Type II",
    "Type III": "Type III",
};

// Badge file lookup delegated to BADGE_META (imported from utils/badgeMeta)

function COA({ userId, setHeaderTitle, setCOAProcessing }) {
    const { currentLicense } = useLicense();
    const { registerRefreshCallback } = useCoaJobs();
    const navigate = useNavigate();
    const { coaId, licenseId, locationId } = useParams();
    const isOwner = parseInt(licenseId) === currentLicense?.id;
    const [coa, setCoa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewer, setViewer] = useState(false);
    const [pdfUrls, setPdfUrls] = useState([]);
    const [imagesOpen, setImagesOpen] = useState(false); 
    const [coaPictures, setCoaPictures] = useState([]);
    const [priceEditing, setPriceEditing] = useState(false);
    const [priceInput, setPriceInput] = useState("");
    const [priceUnitInput, setPriceUnitInput] = useState("g");
    const [showPublishModal, setShowPublishModal] = useState(false); 
    const [orderTestOpen, setOrderTestOpen] = useState(false); // disabled until Lab Orders is ready for production
    const [myPermissions, setMyPermissions] = useState(null);
    const [skuInput, setSkuInput] = useState("");
    const [skuEditing, setSkuEditing] = useState(false);  
    const [weightInput, setWeightInput] = useState("");
    const [weightUnit, setWeightUnit] = useState("lb"); 
    const [weightEditing, setWeightEditing] = useState(false);
    const [descriptionInput, setDescriptionInput] = useState("");
    const [descriptionEditing, setDescriptionEditing] = useState(false);
    const [priceTierEditing, setPriceTierEditing] = useState(false);
    const [priceTiers, setPriceTiers] = useState([]); 
    const [selectedPriceTierId, setSelectedPriceTierId] = useState("");

    const badgeQrRef = useRef(null);

    const hasUnsavedEdits = priceEditing || skuEditing || weightEditing || descriptionEditing || priceTierEditing;
    useUnsavedChanges(hasUnsavedEdits, { message: "You have unsaved edits. Are you sure you want to leave?" });

    // Detect whether the profile panels are currently side by side
    const profilesRowRef = useRef(null);
    const [profilesRowWidth, setProfilesRowWidth] = useState(0);
    useEffect(() => {
        const el = profilesRowRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => setProfilesRowWidth(entry.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, [coa]);

    useEffect(() => {
        setHeaderTitle("Chemotype Data");
    }, []);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => setMyPermissions(res.data))
            .catch(() => setMyPermissions({ can_upload_coa: 1 }));
    }, [currentLicense]);

    const downloadBadge = async () => {
        const pt = (row?.product_type || '').toLowerCase();
        const color = (pt.startsWith('mix') || pt === 'pre-roll') ? 'mix' : (row?.color || '').toLowerCase();
        const meta = BADGE_META[color];
        if (!meta) return;

        const qrEl = badgeQrRef.current?.querySelector('svg');
        if (!qrEl) return;

        const variant = meta.strain;
        const response = await fetch(`/Badges/${variant.file}`);
        const svgText = await response.text();

        const parser = new DOMParser();
        const badgeDoc = parser.parseFromString(svgText, 'image/svg+xml');
        const badgeSvg = badgeDoc.querySelector('svg');

        if (row.product_name) {
            const NS = 'http://www.w3.org/2000/svg';
            const textEl = badgeDoc.createElementNS(NS, 'text');
            textEl.setAttribute('x', '51.03');
            textEl.setAttribute('y', '27.5');
            textEl.setAttribute('text-anchor', 'middle');
            textEl.setAttribute('dominant-baseline', 'central');
            textEl.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
            textEl.setAttribute('font-weight', 'bold');
            textEl.setAttribute('fill', '#231f20');
            const name = row.product_name;
            const fontSize = name.length > 20 ? 3 : name.length > 12 ? 3.8 : 4.5;
            textEl.setAttribute('font-size', String(fontSize));
            textEl.textContent = name;
            badgeSvg.appendChild(textEl);
        }

        const qrClone = qrEl.cloneNode(true);
        qrClone.setAttribute('x', String(variant.qx));
        qrClone.setAttribute('y', String(variant.qy));
        qrClone.setAttribute('width', String(variant.qs));
        qrClone.setAttribute('height', String(variant.qs));
        badgeSvg.appendChild(badgeDoc.importNode(qrClone, true));

        const svgString = new XMLSerializer().serializeToString(badgeSvg);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(row.product_name || 'badge').replace(/\s+/g, '-')}-badge.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleCompoundClick = (key) => navigate(`/compound/${key}`);

    const PRICE_UNIT_LABELS = { g: "/ g", oz: "/ oz", lb: "/ lb", unit: "/ unit", mg_thc: "/ mg THC" };
    const formatPriceUnit = (unit) => PRICE_UNIT_LABELS[unit] || "";

    // After the product table migration, pictures are keyed by product_id.
    // The COA record now carries product_id; fall back to the legacy coa endpoint if absent.
    const fetchCoaPictures = async (coaId, productId) => {
        try {
            const endpoint = productId
                ? `/products/pictures/${productId}`
                : `/coas/pictures/${coaId}`;
            const res = await apiClient.get(endpoint);
            setCoaPictures(res.data || []);
        } catch (err) {
            console.error("Failed to fetch COA pictures:", err);
        }
    };

    const fetchCoa = async () => {
        try {
            const response = await apiClient.get(`/coas/${coaId}${locationId ? `?locationId=${locationId}` : ''}`);
            if (!response.status.toString().startsWith("2")) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = response.data;
            setCoa(data);
            const urls = await apiClient.get(`/coas/pdf/${data[0].id}`);
            const pdfs = [];
            if (data[0].coapdf) pdfs.push(data[0].coapdf);
            pdfs.push(...urls.data.map((u) => u.link).filter(Boolean));
            setPdfUrls(pdfs);
            await fetchCoaPictures(data[0].id, data[0].product_id);
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch COA:", error);
        }
    };

    useEffect(() => {
        return registerRefreshCallback(fetchCoa);
    }, [registerRefreshCallback]);

    useEffect(() => {
        fetchCoa();
    }, [coaId]);

    const handleSaveInventory = async (fields) => {
        await apiClient.patch(`/products/${row.product_id}/inventory`, fields);
        setCoa((prev) => [{ ...prev[0], ...fields }, ...prev.slice(1)]);
    };

    const handleSavePrice = async () => {
        const newPrice = parseFloat(priceInput);
        if (isNaN(newPrice)) return;
        await apiClient.post(`/showroom/set-price`, { item_id: row.item_id, price: newPrice, price_unit: priceUnitInput });
        setCoa((prev) => [{ ...prev[0], price: newPrice, price_unit: priceUnitInput }, ...prev.slice(1)]);
        setPriceEditing(false);
    };

    const handleSaveSku = async () => {
        const trimmed = skuInput.trim();
        if (!trimmed) return;
        await handleSaveInventory({ sku: trimmed });
        setSkuEditing(false);
    };

    const handleSaveDescription = async () => {
        const trimmed = descriptionInput.trim();
        await handleSaveInventory({ description: trimmed || null });
        setDescriptionEditing(false);
    };

    const openPriceTierEdit = async () => {
        setSelectedPriceTierId(row.price_tier_id ?? "");
        if (!priceTiers.length && currentLicense?.licenseNo) {
            try {
                const res = await apiClient.get(`/pricing-tiers?licenseNo=${currentLicense.licenseNo}`);
                setPriceTiers(res.data || []);
            } catch { /* leave empty */ }
        }
        setPriceTierEditing(true);
    };

    const handleSavePriceTier = async () => {
        const idToSave = selectedPriceTierId || null;
        const tierName = priceTiers.find((t) => String(t.id) === String(idToSave))?.name || null;
        await handleSaveInventory({ price_tier_id: idToSave, price_tier_name: tierName });
        setPriceTierEditing(false);
    };

    const handleSaveWeight = async () => {
        const w = parseFloat(weightInput);
        if (isNaN(w) || w <= 0) return;
        // Convert to grams before storing; "units" products pass through unchanged.
        const quantityToStore = weightUnit === "units" ? w : toGrams(w, weightUnit);
        const unitToStore = weightUnit === "units" ? "units" : "g";
        await handleSaveInventory({ quantity: quantityToStore, weight_unit: unitToStore });
        setWeightEditing(false);
    };

    const toProfile = () => navigate(`/profile/${coa[0].licenseNo}`);

    if (loading) return <Loading />;
    if (!coa) return null;

    const row = coa[0];

    // COA detail is restricted to the owning license only.
    // Marketplace product data is available via the showroom popup instead.
    if (!isOwner) {
        return (
            <div className="coa-page">
                <div className="coa-page__permission-denied">
                    <h2>Access Restricted</h2>
                    <p>You do not have permission to view this product's COA data. Only the owning license may access this page.</p>
                </div>
            </div>
        );
    }

    // Derived cannabinoid entries (non-zero only)
    const majorEntries   = filterEntries(MAJOR_CANNABINOIDS, row);
    const minorEntries   = filterEntries(MINOR_CANNABINOIDS, row);
    const syntheticEntries = filterEntries(SYNTHETIC_CANNABINOIDS, row);
    const allCannabinoidEntries = [...majorEntries, ...minorEntries];

    // Derived terpene entries (non-zero only)
    const terpeneEntries = filterEntries(ALL_TERPENES, row);

    // Key metrics
    const totalTHC = parseFloat(
        ((parseFloat(row.THCa) || 0) * 0.877) + (parseFloat(row.delta_9_THC) || 0)
    ).toFixed(2);
    const totalCBD = parseFloat(
        ((parseFloat(row.CBDA) || 0) * 0.877) + (parseFloat(row.CBD) || 0)
    ).toFixed(2);
    const totalCannabinoids = allCannabinoidEntries
        .reduce((sum, e) => sum + e.value, 0)
        .toFixed(2);
    const totalTerpenes = terpeneEntries
        .reduce((sum, e) => sum + e.value, 0)
        .toFixed(2);

    // Shared chart height — only sync when panels are side by side (both 320px min + gap)
    const isSideBySide = profilesRowWidth >= 656;
    const profileChartHeight = isSideBySide
        ? Math.max(
            Math.max(240, allCannabinoidEntries.length * 36 + 60),
            Math.max(240, terpeneEntries.length * 36 + 60)
          )
        : undefined;

    // N/A display logic for key metrics — shown when the entire section has no data
    const displayTHC = (row.THCa == null && row.delta_9_THC == null) ? "N/A" : `${totalTHC}%`;
    const displayCBD = (row.CBDA == null && row.CBD == null) ? "N/A" : `${totalCBD}%`;
    const displayTotalCannabinoids = allCannabinoidEntries.length === 0 ? "N/A" : `${totalCannabinoids}%`;
    const displayTotalTerpenes = terpeneEntries.length === 0 ? "N/A" : `${totalTerpenes}%`;

    // Testing info metadata rows
    const META_FIELDS = [
        { key: "lab_name",                    label: "Lab" },
        { key: "lab_state",                   label: "Lab State" },
        { key: "sample_id",                   label: "Sample ID" },
        { key: "batch_id",                    label: "Batch ID" },
        { key: "date_collected",              label: "Date Collected",  isDate: true },
        { key: "date_received",               label: "Date Received",   isDate: true },
        { key: "date_completed",              label: "Date Completed",  isDate: true },
        { key: "cultivator_producer_name",    label: "Cultivator" },
        { key: "distributor_name",            label: "Distributor" },
    ];

    const metaRows = META_FIELDS
        .map(({ key, label, isDate }) => {
            const raw = row[key];
            if (!raw) return null;
            const value = isDate ? formatDate(raw) : raw;
            if (!value) return null;
            return { label, value };
        })
        .filter(Boolean);

    const cannabinoidType = row.cannabinoid_type;

    const panelStatus = getPanelStatus(row);
    const coaComplete = isCOAComplete(row);
    // Badge download only cares about the THC limit, not full-panel completeness.
    const thcCompliant = isThcCompliant(row);
    const missingPanels = Object.entries(panelStatus)
        .filter(([, { met }]) => !met)
        .map(([key]) => key);

    return (
        <div className="coa-page" data-tutorial="coa-detail">
            {viewer && pdfUrls.length > 0 && <PdfViewerModal urls={pdfUrls} onClose={() => setViewer(false)} />}
            {imagesOpen && (
                <ImageViewerModal
                    coaId={row.product_id || row.id}
                    pictures={coaPictures}
                    onClose={() => setImagesOpen(false)}
                    onUpload={() => fetchCoaPictures(row.id, row.product_id)}
                    canUpload={isOwner}
                    uploadEndpoint={row.product_id ? `/products/upload-picture/${row.product_id}` : undefined}
                />
            )}

            {/* ── Warning banner: missing panels ── */}
            {!coaComplete && (
                <div className="coa-page__panel-warning">⚠ This product is missing data for one or more panels</div>
            )}

            {orderTestOpen && (
                <OrderTestModal
                    coa={row}
                    missingPanels={missingPanels}
                    onClose={() => setOrderTestOpen(false)}
                    fetchCoas={fetchCoa}
                />
            )}

            {/* ── Section 1: Header ── */}
            <div className="coa-page__section coa-page__header">
                <div className="coa-page__header-left">
                    <div className="coa-page__title-row">
                        <ColorDot color={(() => { const pt = (row.product_type || "").toLowerCase(); return (pt === "flower" || pt === "pre-roll") ? row.color : "no color"; })()} />
                        <h2
                            className="coa-page__product-name"
                            style={{
                                color:
                                    row.product_name === "Strain Name Must be Entered Manually"
                                        ? "var(--color-danger)"
                                        : "inherit",
                            }}
                        >
                            {row.product_name}
                        </h2>
                        {cannabinoidType && (
                            <span className="coa-page__type-badge">
                                {TYPE_LABELS[cannabinoidType] || cannabinoidType}
                            </span>
                        )}
                    </div>
                    <div className="coa-page__header-meta">
                        {row.cultivator_producer_name && <span className="coa-page__meta-chip">{row.cultivator_producer_name}</span>}
                        {row.harvest_date && <span className="coa-page__meta-chip">{new Date(row.harvest_date).toLocaleDateString()}</span>}
                    </div>
                </div>  
                <div className="coa-page__header-right">
                    {isOwner && setCOAProcessing && !!myPermissions?.can_upload_coa && !coaComplete && (
                        <COAUpload
                            setCOAProcessing={setCOAProcessing}
                            fetchCoas={fetchCoa}
                            className="coa-page__action-btn"
                            coaId={row.id} 
                        />
                    )}
                    {isOwner && setCOAProcessing && missingPanels.length > 0 && (
                        <button className="coa-page__action-btn" onClick={() => setOrderTestOpen(true)}>
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                            Order Tests
                        </button>
                    )}
                    {isOwner && pdfUrls.length > 0 && (
                        <button className="coa-page__view-docs-btn" onClick={() => setViewer(true)}>
                            View COAs
                        </button>
                    )}
                    <button className="coa-page__images-btn" onClick={() => setImagesOpen(true)}>
                        Images{coaPictures.length > 0 ? ` (${coaPictures.length})` : ""}
                    </button>
                    {(() => { const pt = (row?.product_type || '').toLowerCase(); const bc = (pt.startsWith('mix') || pt === 'pre-roll') ? 'mix' : (row?.color || '').toLowerCase(); return BADGE_META[bc]; })() && thcCompliant && !!myPermissions?.can_upload_coa && currentLicense?.licenseNo === row.licenseNo && (
                        <>
                            {/* Hidden QR code — grabbed by downloadBadge via badgeQrRef */}
                            <div ref={badgeQrRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
                                <QRCodeSVG
                                    value={((row?.product_type || '').toLowerCase().startsWith('mix') || (row?.product_type || '').toLowerCase() === 'pre-roll')
                                        ? `${window.location.origin}/compliance/${toSlug(row.company)}/mix/${row.product_id}`
                                        : `${window.location.origin}/compliance/${toSlug(row.company)}?product=${row.product_id}`}
                                    size={108}
                                    level="M"
                                    includeMargin={false}
                                    fgColor="#0f172a"
                                    bgColor="#ffffff"
                                />
                            </div>
                            <button className="coa-page__action-btn" onClick={downloadBadge}>
                                Download Badge
                            </button>
                        </>
                    )}
                </div>
            </div> 

            {/* ── Section 2: Key Metrics Row ── */}
            <div className="coa-page__metrics-row"> 
                <div className="coa-page__metric-card">
                    <div className="coa-page__metric-value">{displayTHC}</div>
                    <div className="coa-page__metric-label">Total THC</div>
                </div>
                <div className="coa-page__metric-card">
                    <div className="coa-page__metric-value">{displayCBD}</div>
                    <div className="coa-page__metric-label">Total CBD</div>
                </div>
                <div className="coa-page__metric-card"> 
                    <div className="coa-page__metric-value">{displayTotalCannabinoids}</div>
                    <div className="coa-page__metric-label">Total Cannabinoids</div>
                </div>
                <div className="coa-page__metric-card">
                    <div className="coa-page__metric-value">{displayTotalTerpenes}</div>
                    <div className="coa-page__metric-label">Total Terpenes</div> 
                </div>
            </div>

            {/* ── Section 3: Product Details ── */}
            {/* Shown to everyone when on marketplace; only owner sees Add to Marketplace when not listed */}
            {row.item_id ? (
                <div className="coa-page__section coa-page__wholesale-card">
                    {isOwner ? (
                        <div className="coa-page__details-header-row">
                            <h3 className="coa-page__section-title" style={{ margin: 0 }}>Product Details</h3>
                            <span className="coa-page__details-editable-badge">Editable</span>
                        </div> 
                    ) : (
                        <h3 className="coa-page__section-title">Product Details</h3>
                    )}

                    {/* Primary: Price + Quantity */}
                    <div className="coa-page__details-grid coa-page__details-grid--primary">

                        {/* Price */}
                        <div className="coa-page__detail-field coa-page__detail-field--price" data-tutorial="coa-price">
                            <span className="coa-page__detail-label">Price</span>
                            {isOwner && priceEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <input
                                            type="number"
                                            value={priceInput}
                                            onChange={(e) => setPriceInput(e.target.value)}
                                            className="coa-page__detail-input"
                                            autoFocus
                                        />
                                        <select
                                            value={priceUnitInput}
                                            onChange={(e) => setPriceUnitInput(e.target.value)}
                                            className="coa-page__detail-unit-select"
                                        >
                                            <option value="g">/ g</option>
                                            <option value="oz">/ oz</option>
                                            <option value="lb">/ lb</option>
                                            <option value="unit">/ unit</option>
                                            <option value="mg_thc">/ mg THC</option>
                                        </select>
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSavePrice}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setPriceEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.price != null ? (
                                            <>{`$${row.price}`}{row.price_unit && <span className="coa-page__price-unit-label"> {formatPriceUnit(row.price_unit)}</span>}</>
                                        ) : (
                                            <span className="coa-page__detail-value--empty">—</span>
                                        )}
                                    </span>
                                    {isOwner && (
                                        <button className="coa-page__detail-btn" onClick={() => { setPriceInput(row.price ?? ""); setPriceUnitInput(row.price_unit || "g"); setPriceEditing(true); }}>Edit</button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Quantity */}
                        <div className="coa-page__detail-field coa-page__detail-field--quantity" data-tutorial="coa-weight">
                            <span className="coa-page__detail-label">Quantity</span>
                            {isOwner && weightEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <input
                                            type="number"
                                            value={weightInput}
                                            onChange={(e) => setWeightInput(e.target.value)}
                                            className="coa-page__detail-input"
                                            min="0"
                                            step="0.01"
                                            autoFocus
                                        />
                                        <select
                                            value={weightUnit}
                                            onChange={(e) => setWeightUnit(e.target.value)}
                                            className="coa-page__detail-unit-select"
                                        >
                                            <option value="g">g</option>
                                            <option value="oz">oz</option>
                                            <option value="lb">lb</option>
                                            <option value="units">units</option>
                                        </select>
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveWeight}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setWeightEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.quantity ? (
                                            row.weight_unit === "units" ? `${row.quantity} units` : formatWeightFromGrams(row.quantity)
                                        ) : (
                                            <span className="coa-page__detail-value--empty">—</span>
                                        )}
                                    </span>
                                    {isOwner && (
                                        <button className="coa-page__detail-btn" onClick={() => { setWeightInput(row.quantity || ""); setWeightUnit(row.weight_unit || "lb"); setWeightEditing(true); }}>Edit</button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="coa-page__details-divider" />

                    {/* Secondary: SKU + Contact */}
                    <div className="coa-page__details-grid coa-page__details-grid--secondary">

                        {/* SKU */}
                        {(isOwner || row.sku) && (
                            <div className="coa-page__detail-field coa-page__detail-field--sku">
                                <span className="coa-page__detail-label">SKU</span>
                                {isOwner && skuEditing ? (
                                    <div className="coa-page__detail-edit-row">
                                        <div className="coa-page__detail-input-group">
                                            <input
                                                type="text"
                                                value={skuInput}
                                                onChange={(e) => setSkuInput(e.target.value)}
                                                className="coa-page__detail-input"
                                                maxLength={50}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="coa-page__detail-edit-actions">
                                            <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveSku}>Save</button>
                                            <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setSkuEditing(false)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="coa-page__detail-display-row">
                                        <span className="coa-page__detail-value">
                                            {row.sku || <span className="coa-page__detail-value--empty">—</span>}
                                        </span>
                                        {isOwner && (
                                            <button className="coa-page__detail-btn" onClick={() => { setSkuInput(row.sku || ""); setSkuEditing(true); }}>Edit</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contact */}
                        {row.marketplace_contact_email && (
                            <div className="coa-page__detail-field coa-page__detail-field--contact">
                                <span className="coa-page__detail-label">Contact</span>
                                {isOwner ? (
                                    <span className="coa-page__detail-value">{row.marketplace_contact_email}</span>
                                ) : (
                                    <a href={`mailto:${row.marketplace_contact_email}`} className="coa-page__detail-link">
                                        {row.marketplace_contact_email}
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Description */}
                        {(isOwner || row.description) && (
                            <div className="coa-page__detail-field coa-page__detail-field--description">
                                <span className="coa-page__detail-label">Description</span>
                                {isOwner && descriptionEditing ? (
                                    <div className="coa-page__detail-edit-row">
                                        <div className="coa-page__detail-input-group">
                                            <textarea
                                                value={descriptionInput}
                                                onChange={(e) => setDescriptionInput(e.target.value)}
                                                className="coa-page__detail-input coa-page__detail-textarea"
                                                autoFocus
                                                rows={3}
                                            />
                                        </div>
                                        <div className="coa-page__detail-edit-actions">
                                            <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveDescription}>Save</button>
                                            <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setDescriptionEditing(false)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="coa-page__detail-display-row">
                                        <span className="coa-page__detail-value">
                                            {row.description || <span className="coa-page__detail-value--empty">—</span>}
                                        </span>
                                        {isOwner && (
                                            <button className="coa-page__detail-btn" onClick={() => { setDescriptionInput(row.description || ""); setDescriptionEditing(true); }}>Edit</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Price Tier */}
                        {isOwner && (
                            <div className="coa-page__detail-field coa-page__detail-field--price-tier">
                                <span className="coa-page__detail-label">Price Tier</span>
                                {priceTierEditing ? (
                                    <div className="coa-page__detail-edit-row">
                                        <div className="coa-page__detail-input-group">
                                            <select
                                                value={selectedPriceTierId}
                                                onChange={(e) => setSelectedPriceTierId(e.target.value)}
                                                className="coa-page__detail-input"
                                                autoFocus
                                            >
                                                <option value="">None</option>
                                                {priceTiers.map((t) => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="coa-page__detail-edit-actions">
                                            <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSavePriceTier}>Save</button>
                                            <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setPriceTierEditing(false)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="coa-page__detail-display-row">
                                        <span className="coa-page__detail-value">
                                            {row.price_tier_name || <span className="coa-page__detail-value--empty">—</span>}
                                        </span>
                                        <button className="coa-page__detail-btn" onClick={openPriceTierEdit}>Edit</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : isOwner ? (
                <div className="coa-page__section coa-page__wholesale-card">
                    <div className="coa-page__details-header-row">
                        <h3 className="coa-page__section-title" style={{ margin: 0 }}>Product Details</h3>
                        <span className="coa-page__details-editable-badge">Editable</span>
                    </div>

                    {/* Primary: SKU + Quantity */}
                    <div className="coa-page__details-grid coa-page__details-grid--primary">

                        {/* SKU */}
                        <div className="coa-page__detail-field coa-page__detail-field--sku">
                            <span className="coa-page__detail-label">SKU</span>
                            {skuEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <input
                                            type="text"
                                            value={skuInput}
                                            onChange={(e) => setSkuInput(e.target.value)}
                                            className="coa-page__detail-input"
                                            maxLength={50}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveSku}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setSkuEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.sku || <span className="coa-page__detail-value--empty">—</span>}
                                    </span>
                                    <button className="coa-page__detail-btn" onClick={() => { setSkuInput(row.sku || ""); setSkuEditing(true); }}>Edit</button>
                                </div>
                            )}
                        </div>

                        {/* Quantity */}
                        <div className="coa-page__detail-field coa-page__detail-field--quantity" data-tutorial="coa-weight">
                            <span className="coa-page__detail-label">Quantity</span>
                            {weightEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <input
                                            type="number"
                                            value={weightInput}
                                            onChange={(e) => setWeightInput(e.target.value)}
                                            className="coa-page__detail-input"
                                            min="0"
                                            step="0.01"
                                            autoFocus
                                        />
                                        <select
                                            value={weightUnit}
                                            onChange={(e) => setWeightUnit(e.target.value)}
                                            className="coa-page__detail-unit-select"
                                        >
                                            <option value="g">g</option>
                                            <option value="oz">oz</option>
                                            <option value="lb">lb</option>
                                            <option value="units">units</option>
                                        </select>
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveWeight}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setWeightEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.quantity ? (
                                            row.weight_unit === "units" ? `${row.quantity} units` : formatWeightFromGrams(row.quantity)
                                        ) : (
                                            <span className="coa-page__detail-value--empty">—</span>
                                        )}
                                    </span>
                                    <button className="coa-page__detail-btn" onClick={() => { setWeightInput(row.quantity || ""); setWeightUnit(row.weight_unit || "lb"); setWeightEditing(true); }}>Edit</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="coa-page__details-divider" />

                    {/* Secondary: Description + Price Tier */}
                    <div className="coa-page__details-grid coa-page__details-grid--secondary">

                        {/* Description */}
                        <div className="coa-page__detail-field coa-page__detail-field--description">
                            <span className="coa-page__detail-label">Description</span>
                            {descriptionEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <textarea
                                            value={descriptionInput}
                                            onChange={(e) => setDescriptionInput(e.target.value)}
                                            className="coa-page__detail-input coa-page__detail-textarea"
                                            autoFocus
                                            rows={3}
                                        />
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSaveDescription}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setDescriptionEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.description || <span className="coa-page__detail-value--empty">—</span>}
                                    </span>
                                    <button className="coa-page__detail-btn" onClick={() => { setDescriptionInput(row.description || ""); setDescriptionEditing(true); }}>Edit</button>
                                </div>
                            )}
                        </div>

                        {/* Price Tier */}
                        <div className="coa-page__detail-field coa-page__detail-field--price-tier">
                            <span className="coa-page__detail-label">Price Tier</span>
                            {priceTierEditing ? (
                                <div className="coa-page__detail-edit-row">
                                    <div className="coa-page__detail-input-group">
                                        <select
                                            value={selectedPriceTierId}
                                            onChange={(e) => setSelectedPriceTierId(e.target.value)}
                                            className="coa-page__detail-input"
                                            autoFocus
                                        >
                                            <option value="">None</option>
                                            {priceTiers.map((t) => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="coa-page__detail-edit-actions">
                                        <button className="coa-page__detail-btn coa-page__detail-btn--save" onClick={handleSavePriceTier}>Save</button>
                                        <button className="coa-page__detail-btn coa-page__detail-btn--cancel" onClick={() => setPriceTierEditing(false)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="coa-page__detail-display-row">
                                    <span className="coa-page__detail-value">
                                        {row.price_tier_name || <span className="coa-page__detail-value--empty">—</span>}
                                    </span>
                                    <button className="coa-page__detail-btn" onClick={openPriceTierEdit}>Edit</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="coa-page__details-divider" />

                    {/* Add to Marketplace — disabled without quantity or incomplete panels */}
                    <div className="coa-page__wholesale-unpublished" data-tutorial="coa-publish-btn">
                        <button
                            className={`coa-page__publish-btn${coaComplete && row.quantity > 0 ? "" : " coa-page__publish-btn--disabled"}`}
                            disabled={!coaComplete || !row.quantity || row.quantity <= 0}
                            title={
                                !coaComplete
                                    ? "Complete full panel testing to upload to marketplace"
                                    : !row.quantity || row.quantity <= 0
                                    ? "Set a quantity greater than 0 to enable"
                                    : undefined
                            }
                            onClick={coaComplete && row.quantity > 0 ? () => setShowPublishModal(true) : undefined}
                        >
                            Add to Marketplace
                        </button>
                    </div>
                </div>
            ) : null}
            {showPublishModal && <CoaToShowroom coa={row} productId={row.product_id} setPublishTab={setShowPublishModal} fetchCoas={fetchCoa} />}

            {row.product_id && <ProductGrowingDetails productId={row.product_id} canEdit={isOwner} />}

            {/* ── Section 4: Panel Coverage ── */}
            <div className="coa-page__section" data-tutorial="coa-panel-coverage">
                <h3 className="coa-page__section-title">Panel Coverage</h3>
                <div className="coa-page__panel-grid">
                    {Object.entries(PANEL_LABELS).map(([key, label]) => {
                        const { met, isSafetyPanel } = panelStatus[key];
                        const safetyValue = isSafetyPanel ? row[key] || null : null;

                        let panelValue = null;
                        if (key === "cannabinoids" && allCannabinoidEntries.length > 0) {
                            panelValue = `${totalCannabinoids}%`;
                        } else if (key === "terpenes" && terpeneEntries.length > 0) {
                            panelValue = `${totalTerpenes}%`;
                        } else if (key === "moisture" && row.moisture_content != null) {
                            const m = parseFloat(row.moisture_content);
                            if (!isNaN(m)) panelValue = `${m.toFixed(2)}%`;
                        }

                        return (
                            <div
                                key={key}
                                className={`coa-page__panel-card${met ? "" : " coa-page__panel-card--missing"}`}
                            >
                                <span
                                    className={`coa-page__panel-status-icon${met ? " coa-page__panel-status-icon--pass" : " coa-page__panel-status-icon--fail"}`}
                                >
                                    {met ? "✓" : "✗"}
                                </span>
                                <span className="coa-page__panel-label">{label}</span>
                                {isSafetyPanel && safetyValue && (
                                    <span
                                        className={`coa-page__quality-badge ${qualityBadgeClass(safetyValue)} coa-page__panel-badge`}
                                    >
                                        {safetyValue}
                                    </span>
                                )}
                                {panelValue && (
                                    <span className="coa-page__quality-badge coa-page__quality-badge--neutral coa-page__panel-badge">
                                        {panelValue}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Sections 4 & 5: Cannabinoid and Terpene Profiles (side by side) ── */}
            <div className="coa-page__profiles-row" ref={profilesRowRef} data-tutorial="coa-profiles-row">
                {allCannabinoidEntries.length > 0 && (
                    <div className="coa-page__section coa-page__profile-panel">
                        <h3 className="coa-page__section-title">Cannabinoid Profile</h3>
                        <CBarChart entries={allCannabinoidEntries} onBarClick={handleCompoundClick} color={getColor((() => { const pt = (row.product_type || "").toLowerCase(); return (pt === "flower" || pt === "pre-roll") ? row.color : "no color"; })())} height={profileChartHeight} />
                    </div>
                )}
                <div className="coa-page__section coa-page__profile-panel">
                    <h3 className="coa-page__section-title">Terpene Profile</h3>
                    {terpeneEntries.length > 0 ? (
                        <TBarChart entries={terpeneEntries} onBarClick={handleCompoundClick} color={getColor((() => { const pt = (row.product_type || "").toLowerCase(); return (pt === "flower" || pt === "pre-roll") ? row.color : "no color"; })())} height={profileChartHeight} />
                    ) : (
                        <span className="coa-page__no-data">No Terpene Data</span>
                    )}
                </div>
            </div>

            {/* ── Section 8: Testing Information ── */}
            {metaRows.length > 0 && (
                <div className="coa-page__section">
                    <h3 className="coa-page__section-title">Testing Information</h3>
                    <div className="coa-page__meta-grid">
                        {metaRows.map(({ label, value }) => (
                            <div key={label} className="coa-page__meta-field">
                                <span className="coa-page__meta-label">{label}</span>
                                <span className="coa-page__meta-value">{value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default COA;
